<template>
	<div
		:class="[
			'rich-text',
			blok.textAlign,
			textClass,
			hasFontSize(textClass) && 'rich-text--sized'
		]"
		:style="{
			maxWidth: blok.max_width ? `${blok.max_width}px` : ''
		}"
	>
		<StoryblokRichText :doc="blok.content" :resolvers="resolvers" />
	</div>
</template>

<script setup lang="ts">
import type { BlokRichText } from "~/types/storyblok/storyblok";
import type { StoryblokRichTextNode } from "@storyblok/richtext";
import {
	hasFontSize,
	styleClasses
} from "~/layers/base/utils/blokStyleOptions";

interface Props {
	blok: BlokRichText;
}
const { blok } = defineProps<Props>();

// Weight/alignment entries apply as-is; only a size switches paragraphs
// from the template's fixed size to the wrapper's (`rich-text--sized`).
const textClass = computed(() => styleClasses(blok.text_styles));

const getTextAlignStyle = (attrs?: StoryblokRichTextNode["attrs"]) => {
	const textAlign = attrs?.textAlign || attrs?.text_align || attrs?.align;

	return textAlign ? { textAlign } : undefined;
};

const resolvers = {
	paragraph: ({ children, attrs }: StoryblokRichTextNode) =>
		h("p", { style: getTextAlignStyle(attrs) }, children),
	heading: ({ children, attrs }: StoryblokRichTextNode) => {
		const level = Number(attrs?.level);
		const tag = level >= 1 && level <= 6 ? `h${level}` : "h2";

		return h(tag, { style: getTextAlignStyle(attrs) }, children);
	},
	bullet_list: ({ children, attrs }: StoryblokRichTextNode) =>
		h(
			"ul",
			{
				class: "list-disc pl-5",
				style: getTextAlignStyle(attrs)
			},
			children
		),
	ordered_list: ({ children, attrs }: StoryblokRichTextNode) =>
		h(
			"ol",
			{
				class: "list-decimal pl-5",
				style: getTextAlignStyle(attrs)
			},
			children
		),
	list_item: ({ children, attrs }: StoryblokRichTextNode) =>
		h("li", { style: getTextAlignStyle(attrs) }, children),
	table: ({ children, attrs }: StoryblokRichTextNode) =>
		h("table", { style: getTextAlignStyle(attrs) }, children),
	tableRow: ({ children, attrs }: StoryblokRichTextNode) =>
		h("tr", { style: getTextAlignStyle(attrs) }, children),
	tableCell: ({ children, attrs }: StoryblokRichTextNode) =>
		h("td", { style: getTextAlignStyle(attrs) }, children),
	tableHeader: ({ children, attrs }: StoryblokRichTextNode) =>
		h("th", { style: getTextAlignStyle(attrs) }, children)
};
</script>
